import React from "react";
import { cn } from "@/lib/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface FaqsData {
  sectionName: string;
  title: string;
  sections: any[];
}

export interface FaqsProps {
  data: FaqsData;
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function Faqs({ data }: FaqsProps) {
  // Split FAQs into two columns
  const { sectionName, title, sections } = data;
  const mid = Math.ceil(sections.length / 2);
  const leftFaqs = sections.slice(0, mid);
  const rightFaqs = sections.slice(mid);

  const sectionId = data.sectionName.replace(/\s+/g, "-");

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className={cn("w-full flex flex-col bg-accent-color-three")}
    >
      <div
        className={cn("py-12 desktop:py-16 gap-12 mx-auto flex flex-col")}
        style={{ maxWidth: "85vw" }}
      >
        <div className="flex flex-col gap-2 mb-8">
          <m.div
            className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {title}
          </m.div>
        </div>
        <div className="w-full flex flex-col desktop:flex-row gap-6 md:gap-12">
          {/* Left Column */}
          <m.div
            className="w-full desktop:w-1/2 flex flex-col gap-12"
            variants={fadeIn}
          >
            {leftFaqs.map((faq, idx) => (
              <m.div
                key={idx}
                className="flex flex-col gap-4"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="font-sans text-h3-mobile md:text-h3-tablet desktop:text-h3-desktop font-semibold tracking-widest uppercase text-[#F6DFBF] mb-2">
                  {faq.item.title}
                </div>
                <div
                  className="font-sans text-paragraph-medium text-white [&_*:is(a)]:underline [&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6 [&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6"
                  dangerouslySetInnerHTML={{ __html: faq.item.text }}
                ></div>
              </m.div>
            ))}
          </m.div>
          {/* Right Column */}
          <m.div
            className="w-full desktop:w-1/2 flex flex-col gap-12"
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {rightFaqs.map((faq, idx) => (
              <m.div
                key={idx}
                className="flex flex-col gap-4"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="font-sans text-h3-mobile md:text-h3-tablet desktop:text-h3-desktop font-semibold tracking-widest uppercase text-[#F6DFBF] mb-2">
                  {faq.item.title}
                </div>
                <div
                  className="font-sans text-paragraph-medium text-white [&_*:is(a)]:underline [&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6 [&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6"
                  dangerouslySetInnerHTML={{ __html: faq.item.text }}
                ></div>
              </m.div>
            ))}
          </m.div>
        </div>
      </div>
    </section>
    </LazyMotion>
  );
}
